export enum PublicLedgerMessageType {
  PLAIN_TEXT,
  HASHED,
  ENCRYPTED,
}

// Which kind of topic a message goes to: a company's (signed by its signatories), a person's, or nodeStream's own INTERNAL one
export type PublicLedgerTopicType = 'CORPORATE' | 'INDIVIDUAL' | 'INTERNAL';

export type PublicLedgerMessageStatus =
  | 'QUEUED'
  | 'SUBMITTING'
  | 'ENCRYPTION_COMPLETED'
  | 'SUBMITTED'
  | 'SUCCESS'
  | 'REJECTED'
  | 'EXPIRED'
  | 'CANCELLED';

// One ledger message of a message too long for one (each part is its own scheduled transaction)
export interface PublicLedgerMessagePart {
  index: number;
  // The plTransaction now carrying this part (a new one when a failed part is signed again)
  transactionId: string | null;
  sequence: number | null;
  timestamp: string | null;
  url: string | null;
  // Why the part's last attempt failed on the public ledger
  failureReason: string | null;
}

export interface PublicLedgerMessage {
  id: string;
  topicId: string | null;
  companyId?: string | null;
  topicType?: PublicLedgerTopicType | null;
  workflowId: string | null;
  status: PublicLedgerMessageStatus | string;
  type: string;
  sequence: string;
  timestamp: string;
  url?: string | null;
  cancelReason?: string | null;
  parts?: PublicLedgerMessagePart[];
  content: {
    entityId: string | null;
    collection: string | null;
    text: string | null;
  };
}

export interface PublicLedgerTopic {
  id: string;
  companyId: string | null;
  personId: string | null;
  plOperatingAccountId: string;
  topicId: string;
  type: string;
  url: string;
  adminKey: {
    name: string;
    keyType: string;
    threshold: number;
    keys: any[];
  };
  submitKey: {
    name: string;
    keyType: string;
    threshold: number;
    keys: any[];
    // Set while a change to who signs the topic is open (#38): company topic messages wait for it
    awaitingUpdate?: boolean;
  };
}

export interface PublicLedgerTransaction {
  id: string;
  companyId: string;
  transactionId: string;
  scheduleId: string;
  consensusService: {
    topicId: string;
    messageId: string;
  };
  status: PublicLedgerTransactionStatus | string;
  type: string;
  topicType?: PublicLedgerTopicType | null;
  signatories: string[];
  signatures?: PublicLedgerSignature[];
  rejection?: PublicLedgerRejection | null;
  cancelReason?: string | null;
  expiresAt?: string | null;
  executedAt?: string | null;
  // The public ledger's result code once the scheduled transaction has executed, SUCCESS or the reason it failed
  resultCode?: string | null;
  // Set on a message part signed again after it failed: the part, and the failed transaction this one replaces
  partIndex?: number | null;
  replacesTransactionId?: string | null;
  serialisedTransaction: string;
  result: string;
  // A signatory change (UPDATE_ACCOUNT_TRANSACTION): the rule proposed, who asked for it, and why it ended unapplied
  proposedKey?: unknown;
  requestedBy?: string | null;
  outcomeReason?: string | null;
  // A transfer (TRANSFER_TRANSACTION: an automatic top-up or a credit delivery): what moves between which accounts, and the
  // platform's seed to the operating account when it couldn't pay the fee. `kind` is AUTO_TOP_UP for an automatic top-up; a
  // credit delivery is a transfer from the platform to the company account
  transfer?: {
    fromAccountId?: string | null;
    toAccountId?: string | null;
    tinybars?: string | null;
    kind?: string | null;
  } | null;
  seed?: {
    transactionId?: string | null;
    tinybars?: string | null;
    status?: string | null;
  } | null;
  // What a transaction cost and which account paid it (#25 phase 5b, server-written; `tinybars` null until it has been read),
  // and what its signatories' ScheduleSigns cost the operating account (`signTinybars`, once a scheduled row has ended)
  fee?: { tinybars?: string | null; payerAccountId?: string | null; signTinybars?: string | null } | null;
  // The automatic payments approval (APPROVE_ALLOWANCE, #25): the company account lets its operating account (spender) draw.
  // `payer` says who pays its signing: nodeStream's platform account, for the set-up and every re-sign (DECISION 4.1)
  allowance?: {
    ownerAccountId?: string | null;
    spenderAccountId?: string | null;
    memo?: string | null;
    // PLATFORM: set-up and re-sign; OPERATING: a change of amounts (#25 phase 6), whose schedule the operating account
    // creates and pays, and whose approval it pays when it runs (G2 a)
    payer?: 'PLATFORM' | 'OPERATING' | string | null;
    // Who pays the scheduled approval when it runs
    payerAccountId?: string | null;
    // Who created the schedule (and is its admin)
    creatorAccountId?: string | null;
    // Who pays every ScheduleSign: nodeStream's platform account, always (DECISION 4.1 a)
    signPayerAccountId?: string | null;
    // The amount: the ledger's maximum, or (#40) '0' for a stop of automatic payments
    tinybars?: string | null;
  } | null;
  // A change of the automatic payment card (CARD_CHANGE, #25 phase 6 round 15): a scheduled record of 1 tinybar from the
  // company account to its operating account, whose memo says who changed the card (no card details). The operating account
  // creates it and pays it when it runs; nodeStream's platform account pays every ScheduleSign. The accounts are filled in
  // once it is scheduled
  cardChange?: PublicLedgerCardChange | null;
}

export interface PublicLedgerCardChange {
  // The change-card or Payment failed workflow
  workflowId?: string | null;
  autoPaymentId?: string | null;
  companyAccountId?: string | null;
  operatingAccountId?: string | null;
  memo?: string | null;
  payer?: 'OPERATING' | string | null;
  creatorAccountId?: string | null;
  payerAccountId?: string | null;
  signPayerAccountId?: string | null;
}

export type PublicLedgerTransactionStatus =
  | 'PENDING'
  | 'COLLECTING_SIGNATURES'
  | 'SIGNED'
  | 'SUCCESS'
  | 'FAILED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'CANCELLED';

export interface PublicLedgerSignature {
  personId: string;
  publicKey: string;
  signedAt: string;
}

/**
 * Whether reject-scheduled-transaction's result says the signing was rejected. The lambda ends with
 * `{ status: 'REJECTED', transactionIds }` (it has no `code`); a refusal never reaches here, as callLambda throws on it.
 */
export function isRejectedResult(res: unknown): boolean {
  // A non-null object is safe to read one optional property from
  return typeof res === 'object' && res !== null && (res as { status?: unknown }).status === 'REJECTED';
}

// One signatory's rejection, which ended the signing. The reason stays in nodeStream; it is never put on the public ledger
export interface PublicLedgerRejection {
  personId: string | null;
  reason: string | null;
  rejectedAt: string | null;
}

// A Buy nodeStream credits purchase, as the user policy projects it to the company's members (no lastError)
export interface PublicLedgerCreditPurchase {
  id: string;
  companyId: string;
  jobId: string | null;
  workflowId: string | null;
  network: 'TESTNET' | 'MAINNET';
  status: string | null;
  quote?: { id?: string | null; unitPricePence?: number | null; expiresAt?: string | null } | null;
  credits?: number | null;
  amountPence?: number | null;
  currency?: string | null;
  paidAt?: string | null;
  // When the payment was fulfilled; the credits reach the account with the delivery's own transfer row. Read by the
  // company's public ledger signatories only (the user policy's second read, by the row's signatoriesTeamId)
  deliveredAt?: string | null;
  creditsBought?: number | null;
  creditsDelivered?: number | null;
  creditsOwed?: number | null;
  creditsRefunded?: number | null;
}

export default interface PublicLedgerOperatingAccount {
  id: string;
  companyId: string | null;
  workflowsId: string[] | null;
  publicKey: string | null;
  accountId: string | null;
  url: string | null;
  signatureRequirement: {
    threshold: number | 0;
    signatories: {
      id: string;
      publicKey: string;
    }[];
  };
  topicId: string | null;
}
